/**
 * HudTag — small clip-bevel'd tag with monospace text.
 *
 *   <HudTag>CRITICAL</HudTag>
 *   <HudTag tone="warn">WARNING</HudTag>
 *   <HudTag tone="success">ONLINE</HudTag>
 *
 * Used for severities, statuses, classifications. Replaces the older
 * pill-shaped badges from the SaaS aesthetic with a more tactical chip.
 */
import clsx from 'clsx';

type Tone = 'accent' | 'alert' | 'warn' | 'success' | 'sky' | 'neutral';

interface Props {
  children: React.ReactNode;
  tone?: Tone;
  className?: string;
}

const TONE_CLASSES: Record<Tone, string> = {
  accent: 'bg-accent/15 text-accent border-accent/30',
  alert: 'bg-alert/15 text-alert border-alert/40',
  warn: 'bg-warn/15 text-warn border-warn/40',
  success: 'bg-success/15 text-success border-success/40',
  sky: 'bg-sky/15 text-sky border-sky/40',
  neutral: 'bg-iron-700 text-iron-200 border-iron-500',
};

export default function HudTag({ children, tone = 'neutral', className }: Props) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider clip-bevel-sm border',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
